import { useState, useRef } from 'react'
import { IconChevronDown } from '@tabler/icons-react'

import { getLanguageCode } from 'utils/translation'
import { useOutsideClick } from 'hooks/use-outside-click'
import Text from 'components/ui/text'
import { cn } from 'utils/classMerge'

import { LANGUAGE_CODE_LIST } from './consts'

const pathWithLang = (lang: string): string => {
  const { origin, pathname, search } = window.location
  const newPathname = pathname.replace(
    /^\/(en|pt|de|es|it|fr|nl|ja|zh)?(\/|$)/,
    `/${lang}$2`,
  )
  return `${origin}${newPathname}${search}`
}

const LanguageSelector = () => {
  const langRef = useRef<HTMLButtonElement | null>(null)
  const [listening, setListening] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const languageCode = getLanguageCode()
  const toggle = () => setIsOpen((prev) => !prev)

  useOutsideClick(listening, setListening, langRef, setIsOpen)

  return (
    <div className="tw-relative">
      <button
        ref={langRef}
        data-testid="nav-active-lang"
        className="tw-flex tw-p-0 tw-m-0 tw-items-center tw-w-[60px] tw-h-[40px] tw-cursor-pointer tw-bg-transparent tw-border-none tw-gap-[5px]"
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={toggle}
        type="button"
      >
        <Text
          as="span"
          className="tw-text-sm tw-font-medium tw-m-0 tw-p-0 tw-text-zinc-600"
        >{`${languageCode}-menu-short`}</Text>
        <IconChevronDown
          size={16}
          stroke={3}
          className={cn('tw-transition-all tw-stroke-orange', {
            'tw-rotate-180': isOpen,
            'tw-stroke-zinc-600': !isOpen,
          })}
        />
      </button>
      <ul
        data-testid="nav-lang-list"
        className={cn(
          'tw-absolute tw-z-[110] tw-top-[calc(100%+10px)] tw-m-0 tw-p-0 tw-py-5 tw-opacity-100 tw-visible  tw-bg-white tw-min-w-[145px] tw-rounded-sm tw-shadow-[0_20px_29px_0_rgba(207,67,20,0.5)]',
          {
            'tw-opacity-0 tw-invisible': !isOpen,
          },
        )}
      >
        {LANGUAGE_CODE_LIST.map(({ code, label }) => (
          <li
            data-testid="nav-lang-item"
            key={code}
            className={cn(
              'tw-whitespace-nowrap tw-border-0 tw-flex tw-items-center tw-w-full',
            )}
          >
            <Text
              as="span"
              className={cn(
                'tw-text-xs tw-font-normal tw-text-zinc-400 tw-py-[11px] tw-px-[30px] tw-min-h-[25px]',
                {
                  'tw-cursor-pointer tw-text-zinc-600 hover:tw-text-orange':
                    languageCode !== code,
                },
              )}
              onClick={() => {
                if (languageCode === code) return
                setIsOpen(false)
                window.location.href = pathWithLang(code)
              }}
            >
              {label}
            </Text>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default LanguageSelector
